/**
 * Fitting Renderer - Draws duct fittings (elbows, tees) on canvas
 */

import { DUCT_TYPES } from '../../constants/hvacOptions';

/**
 * Draw a single duct fitting
 * Canvas context should already have scale transform applied
 * All coordinates are in world space (inches)
 */
export function drawFitting(ctx, fitting, scale, isSelected = false, isPreview = false) {
  const ductType = DUCT_TYPES[fitting.ductType] || DUCT_TYPES.supply;
  const size = fitting.size || 6;

  ctx.save();

  // Move to fitting position and apply rotation
  ctx.translate(fitting.x, fitting.y);
  ctx.rotate((fitting.rotation || 0) * Math.PI / 180);

  // Set styles
  const fillOpacity = isPreview ? 0.3 : 0.6;
  ctx.fillStyle = isSelected
    ? `rgba(0, 200, 255, ${fillOpacity})`
    : `${ductType.color}${Math.round(fillOpacity * 255).toString(16).padStart(2, '0')}`;
  ctx.strokeStyle = isSelected ? '#00c8ff' : ductType.strokeColor;
  ctx.lineWidth = (isSelected ? 2 : 1.5) / scale;

  // Draw based on fitting type
  switch (fitting.type) {
    case 'elbow':
    case 'elbow90':
      drawElbow90(ctx, size, scale);
      break;
    case 'elbow45':
      drawElbow45(ctx, size, scale);
      break;
    case 'tee':
      drawTee(ctx, size, scale);
      break;
    default:
      drawElbow90(ctx, size, scale);
  }

  // Draw selection handles if selected
  if (isSelected) {
    drawFittingHandles(ctx, fitting.type, size, scale);
  }

  ctx.restore();
}

/**
 * Draw 90-degree elbow - simple L-shape
 */
function drawElbow90(ctx, size, scale) {
  const half = size / 2;
  const len = size * 1.2;

  // Draw L-shaped elbow
  ctx.beginPath();
  // Start at bottom of vertical leg
  ctx.moveTo(-half, len);
  ctx.lineTo(-half, -half);
  ctx.lineTo(half, -half);
  ctx.lineTo(half, half);
  // Horizontal leg extends right
  ctx.lineTo(len, half);
  ctx.lineTo(len, -half);
  ctx.lineTo(half, -half);
  // Back down
  ctx.lineTo(half, len);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Draw curved centerline to indicate flow
  ctx.beginPath();
  ctx.strokeStyle = ctx.fillStyle;
  ctx.lineWidth = 1 / scale;
  ctx.setLineDash([3 / scale, 3 / scale]);
  ctx.arc(half, half, size * 0.5, Math.PI, Math.PI * 1.5);
  ctx.stroke();
  ctx.setLineDash([]);
}

/**
 * Draw 45-degree elbow
 */
function drawElbow45(ctx, size, scale) {
  const half = size / 2;
  const len = size * 1.0;
  const angle = Math.PI / 4; // 45 degrees

  ctx.beginPath();
  // Vertical section
  ctx.moveTo(-half, len);
  ctx.lineTo(-half, 0);
  // Angled section
  ctx.lineTo(-half + Math.cos(angle) * len, -Math.sin(angle) * len);
  ctx.lineTo(half + Math.cos(angle) * len, -Math.sin(angle) * len + size);
  // Back
  ctx.lineTo(half, 0);
  ctx.lineTo(half, len);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

/**
 * Draw Tee fitting
 */
function drawTee(ctx, size, scale) {
  const half = size / 2;
  const len = size * 1.5;

  // Main horizontal run
  ctx.beginPath();
  ctx.rect(-len, -half, len * 2, size);
  ctx.fill();
  ctx.stroke();

  // Branch (vertical going up)
  ctx.beginPath();
  ctx.rect(-half, -half, size, -len);
  ctx.fill();
  ctx.stroke();

  // Draw flow arrows
  ctx.fillStyle = ctx.strokeStyle;
  ctx.globalAlpha = 0.5;

  // Arrow in branch
  const arrowSize = size * 0.3;
  ctx.beginPath();
  ctx.moveTo(0, -half - len + arrowSize);
  ctx.lineTo(-arrowSize, -half - len + arrowSize * 2);
  ctx.lineTo(arrowSize, -half - len + arrowSize * 2);
  ctx.closePath();
  ctx.fill();

  ctx.globalAlpha = 1;
}

/**
 * Draw selection handles for fitting
 */
function drawFittingHandles(ctx, type, size, scale) {
  const handleSize = 4 / scale;

  ctx.fillStyle = '#00c8ff';
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 1 / scale;

  // Center handle
  ctx.beginPath();
  ctx.arc(0, 0, handleSize, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Rotation handle
  const rotHandleDist = size * 1.8;
  ctx.beginPath();
  ctx.arc(0, -rotHandleDist, handleSize / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Line to rotation handle
  ctx.beginPath();
  ctx.moveTo(0, -size / 2);
  ctx.lineTo(0, -rotHandleDist + handleSize / 2);
  ctx.stroke();
}

/**
 * Draw all fittings
 */
export function drawAllFittings(ctx, fittings, scale, selectedFittingId = null) {
  if (!fittings || fittings.length === 0) return;

  // Draw non-selected fittings first
  fittings.forEach((fitting) => {
    if (fitting.id !== selectedFittingId) {
      drawFitting(ctx, fitting, scale, false, false);
    }
  });

  // Draw selected fitting on top
  const selectedFitting = fittings.find((f) => f.id === selectedFittingId);
  if (selectedFitting) {
    drawFitting(ctx, selectedFitting, scale, true, false);
  }
}

/**
 * Draw fitting preview while placing
 */
export function drawFittingPreview(ctx, x, y, fittingType, ductType, size, rotation, scale) {
  const previewFitting = {
    x,
    y,
    type: fittingType,
    ductType,
    size,
    rotation: rotation || 0,
  };

  drawFitting(ctx, previewFitting, scale, false, true);
}

/**
 * Hit test for fitting selection
 */
export function hitTestFitting(worldX, worldY, fitting, scale) {
  const size = fitting.size || 6;

  // Transform click point to fitting's local coordinates
  const dx = worldX - fitting.x;
  const dy = worldY - fitting.y;
  const rotation = (fitting.rotation || 0) * Math.PI / 180;

  // Rotate point back to align with fitting axes
  const localX = dx * Math.cos(-rotation) - dy * Math.sin(-rotation);
  const localY = dx * Math.sin(-rotation) + dy * Math.cos(-rotation);

  // Check if point is within fitting bounds (with padding)
  const padding = Math.max(5, 5 / scale);
  const bound = size * 1.8 + padding;

  return Math.abs(localX) <= bound && Math.abs(localY) <= bound;
}

export default {
  drawFitting,
  drawAllFittings,
  drawFittingPreview,
  hitTestFitting,
};
